import authAxios from 'src/modules/shared/axios/authAxios';
import { AuthToken } from 'src/modules/auth/authToken';
import AuthCurrentTenant from 'src/modules/auth/authCurrentTenant';
import AuthInvitationToken from 'src/modules/auth/authInvitationToken';
import { tenantSubdomain } from '../tenant/tenantSubdomain';

export default class AuthService {
  static async sendEmailVerification() {
    const response = await authAxios.post(
      '/auth/send-email-address-verification-email',
      {
        tenantId: tenantSubdomain.isSubdomain
          ? AuthCurrentTenant.get()
          : undefined,
      },
    );

    return response.data;
  }

  static async sendPasswordResetEmail(email) {
    const response = await authAxios.post(
      '/auth/send-password-reset-email',
      {
        email,
        tenantId: tenantSubdomain.isSubdomain
          ? AuthCurrentTenant.get()
          : undefined,
        isFromOcean: true,
      },
    );

    return response.data;
  }

  static async registerWithEmailAndPassword(
    email,
    password,
  ) {
    const invitationToken = AuthInvitationToken.get();

    const response = await authAxios.post('/auth/sign-up', {
      email,
      password,
      invitationToken,
      tenantId: tenantSubdomain.isSubdomain
        ? AuthCurrentTenant.get()
        : undefined,
      sendEmailVerification: true,
    });

    AuthInvitationToken.clear();

    return response.data;
  }

  // ─── Ocean admin email-MFA login ──────────────────────────────────────────
  // Two-step login for the admin web app. These replace /auth/sign-in for the
  // Ocean site. Every call MUST send credentials so the HttpOnly remember-me
  // cookie is sent/stored (see authAxios + §3/§4 of the implementation guide).

  // Step 1: verify password → backend emails a 6-digit code (no session yet).
  // → { mfaToken, maskedEmail, expiresAt }
  static async adminSignIn(email, password) {
    const response = await authAxios.post(
      '/auth/admin/sign-in',
      { email, password },
      { withCredentials: true },
    );

    return response.data;
  }

  // Step 2: submit the code → backend issues the session JWT (+ Set-Cookie when
  // rememberMe is true). → { token }
  static async adminMfaVerify(mfaToken, code, rememberMe) {
    const response = await authAxios.post(
      '/auth/admin/mfa/verify',
      { mfaToken, code, rememberMe },
      { withCredentials: true },
    );

    return response.data;
  }

  // Email a fresh code for the same challenge. → { expiresAt }
  static async adminMfaResend(mfaToken) {
    const response = await authAxios.post(
      '/auth/admin/mfa/resend',
      { mfaToken },
      { withCredentials: true },
    );

    return response.data;
  }

  // Silent auto-login from the HttpOnly remember-me cookie (no body). The
  // backend rotates the cookie on each success. → { token }
  static async adminSessionRememberMe() {
    const response = await authAxios.post(
      '/auth/admin/session/remember-me',
      {},
      { withCredentials: true },
    );

    return response.data;
  }

  // Explicit logout: revoke the device's remember-me cookie server-side, then
  // drop the local JWT. Best-effort — we still clear the token if it fails.
  static async adminSignOut() {
    try {
      await authAxios.post(
        '/auth/admin/sign-out',
        {},
        { withCredentials: true },
      );
    } catch (error) {
      // ignore — the session is being torn down regardless
    }

    AuthToken.set(null, true);
  }

  static async fetchMe() {
    const response = await authAxios.get('/auth/me');
    return response.data;
  }

  static signout() {
    AuthToken.set(null, true);
  }

  static async updateProfile(data) {
    const body = {
      data,
    };

    const response = await authAxios.put(
      '/auth/profile',
      body,
    );

    return response.data;
  }

  static async changePassword(oldPassword, newPassword) {
    const body = {
      oldPassword,
      newPassword,
    };

    const response = await authAxios.put(
      '/auth/change-password',
      body,
    );

    return response.data;
  }

  static async changePasswordMember(user, newPassword) {
    const body = {
      user,
      newPassword,
    };

    const response = await authAxios.put(
      '/auth/change-password-member',
      body,
    );

    return response.data;
  }

  static async passwordReset(token, password) {
    const response = await authAxios.put(
      '/auth/password-reset',
      {
        token,
        password,
        tenantId: tenantSubdomain.isSubdomain
          ? AuthCurrentTenant.get()
          : undefined,
      },
    );

    return response.data;
  }

  static async verifyEmail(token) {
    const response = await authAxios.put(
      '/auth/verify-email',
      {
        token,
        tenantId: tenantSubdomain.isSubdomain
          ? AuthCurrentTenant.get()
          : undefined,
      },
    );

    return response.data;
  }

  static async socialOnboard() {
    const invitationToken = AuthInvitationToken.get();

    const response = await authAxios.post(
      '/auth/social/onboard',
      {
        invitationToken,
        tenantId: tenantSubdomain.isSubdomain
          ? AuthCurrentTenant.get()
          : undefined,
      },
    );

    AuthInvitationToken.clear();

    return response.data;
  }

  static isSocialOnboardRequested() {
    const urlParams = new URLSearchParams(
      window.location.search,
    );

    return Boolean(urlParams.get('social'));
  }
}
